import { DateText } from '@/components/common/date-time-text';
import { TabContentTableWrapper } from '@/components/tables/tab-content-table-wrapper';
import { EmptyText } from '@/components/typography/empty-text';
import { AttachmentIcon } from '@/components/ui/attachment-icon';
import { Badge } from '@/features/list-views/components/badge';
import { DataTable } from '@/features/list-views/components/data-table';
import { useLocale } from '@/lib/hooks/use-locale';
import type { ColumnDef } from '@tanstack/react-table';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import type { OrderAttachment } from '../hooks/use-order-attachments';
import { useOrderAttachments } from '../hooks/use-order-attachments';
import { AttachmentDownloadButton } from './attachment-download-button';
import { AttachmentPreviewButton } from './attachment-preview-button';

type AttachmentsTableProps = {
    transactionId: string;
};

export const QuoteOrderAttachmentsTab: React.FC<AttachmentsTableProps> = ({ transactionId }) => {
    const { t } = useLocale('transaction-details');

    // Fetch attachments data - only runs when component is mounted (tab is open)
    const { data: attachments = [], isLoading } = useOrderAttachments({ transactionId });

    const columns: ColumnDef<OrderAttachment>[] = useMemo(
        () => [
            {
                accessorKey: 'documentName',
                header: t('attachments__column_document'),
                cell: ({ row }) => (
                    <div className="flex items-center gap-4">
                        <AttachmentIcon fileType="PDF" />
                        <span className="text-sm text-neutral-900">{row.original.documentName}</span>
                    </div>
                ),
                size: 350,
            },
            {
                accessorKey: 'printedAt',
                header: t('attachments__column_date_added'),
                cell: ({ row }) =>
                    row.original.printedAt ? <DateText date={row.original.printedAt} /> : <EmptyText />,
                size: 130,
            },
            {
                accessorKey: 'documentTypeText',
                header: t('attachments__column_type'),
                cell: ({ row }) => (
                    <div className="flex">
                        <Badge color="neutral">{row.original.formularName}</Badge>
                    </div>
                ),
                size: 174,
            },
            {
                id: 'actions',
                header: '',
                cell: ({ row }) => (
                    <div className="flex items-center gap-3">
                        <AttachmentPreviewButton attachment={row.original} />
                        <AttachmentDownloadButton attachment={row.original} />
                    </div>
                ),
                size: 104,
            },
        ],
        [t],
    );

    const table = useReactTable({
        data: attachments as OrderAttachment[],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <TabContentTableWrapper>
            <DataTable isLoading={isLoading} table={table} columns={columns} />
        </TabContentTableWrapper>
    );
};
